import { fetcher, fetcherThrows, useAuthFetch } from "matsuri-hooks"
import { preload } from "swr"
import dayjs from "dayjs"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import { ListingOtaInfo } from "../domain/otaInfo"
import { Reservation, ReservationDetail } from "../domain/reservation"
import { StayType } from "../domain/stayType"
import { m2mCore } from "../endpoints/m2m-core"
import {
    m2mCore_v1,
    GetReservationByIdResponse,
    SplitAndTransferReservationRequest,
    CreateTemporaryTransferReservationRequest
} from "../endpoints/m2m-core.v1"
dayjs.extend(isSameOrBefore)

export const useReservation = (token: string, reservationId: string) => {
    return useAuthFetch<ReservationDetail>(
        token,
        m2mCore.v1.reservationDetail({ id: reservationId }),
        {}
    )
}

export const useReservationById = (token: string, reservationId: string) => {
    return useAuthFetch<GetReservationByIdResponse>(
        token,
        m2mCore_v1.getReservationById({ reservationId }),
        {}
    )
}

export const useReservationWithSuspense = (
    token: string,
    reservationId: string
) => {
    return useAuthFetch<ReservationDetail>(
        token,
        m2mCore.v1.reservationDetail({ id: reservationId }),
        {
            swrConfig: { suspense: true }
        }
    )
}

export const requestGetReservation = (token: string, reservationId: string) => {
    return fetcher<ReservationDetail>(
        m2mCore.v1.reservationDetail({ id: reservationId }),
        {
            token
        }
    )
}

export const preloadReservation = (token: string, reservationId: string) => {
    const url = m2mCore.v1.reservationDetail({ id: reservationId })
    return preload({ url, token }, (key: { url: string; token: string }) =>
        fetcherThrows(key.url, { token: key.token })
    )
}

export const requestTransferReservation = (
    token: string,
    input: {
        reservationId: string
        roomTypeId: string
        listingId: string
    }
) => {
    return fetcher(
        m2mCore.v1.transferRoomTypeReservation({ id: input.reservationId }),
        {
            token,
            body: JSON.stringify(input),
            method: "POST"
        }
    )
}

export const cancelReservation = (token: string, reservationId: string) => {
    return fetcher(m2mCore.v1.cancelReservation({ id: reservationId }), {
        token,
        method: "POST"
    })
}

export const requestCancelRelocation = (
    token: string,
    fromReservationId: string,
    toReservationId: string
) => {
    return fetcher(
        m2mCore_v1.cancelRelocation({ reservationId: fromReservationId }),
        {
            token,
            body: JSON.stringify({ toReservationId }),
            method: "POST"
        }
    )
}

export const useAssignFailedReservations = (
    token: string,
    condition: {
        search?: boolean
        reason?: string
    }
) => {
    const { data, ...others } = useAuthFetch<{
        reservations?: Reservation[]
    }>(
        token,
        m2mCore.v1.assignFailedReservations({
            search: `${condition.search ?? false}`,
            reason: condition.reason
        }),
        {}
    )

    return {
        ...others,
        data: data?.reservations
    }
}

export const sum = (...arr: number[]) => arr.reduce((x, y) => x + y, 0)

export interface ReservationSalesPriceInput {
    stayType: StayType
    minpakuStaySales: number
    minpakuCleaningSales: number
    minpakuSettlement: number
    minpakuManagementSales: number
    minpakuLinenSales: number
    minpakuUtilitySales: number
    monthlyStaySales: number
    monthlyCleaningSales: number
    monthlySettlement: number
    monthlyUtilitySales: number
    monthlyLinenSales: number
    monthlyManagementSales: number
    rentalAppliance: number
    pickup: number
    food: number
    initialGuaranteeFee: number
    others: number
    commissionFee: number
    // 預かり金。売上合計値には含めない(経費差引後の算出にのみ使う)
    accommodationTax?: number
    breakfast?: number
    depositInitialGuaranteeFee?: number
}

export const calculateReservationStayTypeSalesPrice = (input: {
    stayType: StayType
    minpakuStaySales: number
    minpakuCleaningSales: number
    minpakuSettlement: number
    minpakuManagementSales: number
    minpakuLinenSales: number
    minpakuUtilitySales: number
    monthlyStaySales: number
    monthlyCleaningSales: number
    monthlySettlement: number
    monthlyUtilitySales: number
    monthlyLinenSales: number
    monthlyManagementSales: number
}) => {
    return input.stayType === "minpaku"
        ? sum(
              input.minpakuStaySales,
              input.minpakuCleaningSales,
              input.minpakuSettlement,
              input.minpakuManagementSales,
              input.minpakuUtilitySales,
              input.minpakuLinenSales
          )
        : sum(
              input.monthlyStaySales,
              input.monthlyCleaningSales,
              input.monthlySettlement,
              input.monthlyUtilitySales,
              input.monthlyLinenSales,
              input.monthlyManagementSales
          )
}

export const calculateReservationAdditionalSalesPrice = (input: {
    rentalAppliance: number
    pickup: number
    food: number
    initialGuaranteeFee: number
    others: number
    commissionFee: number
}) => {
    return sum(
        input.rentalAppliance,
        input.pickup,
        input.food,
        input.initialGuaranteeFee,
        input.others,
        input.commissionFee
    )
}

// 預かり金の合計。売上合計値には含めないが、売上合計値(経費差引後)からは差し引く
export const calculateDepositSalesPrice = (input: {
    accommodationTax?: number
    breakfast?: number
    depositInitialGuaranteeFee?: number
}) => {
    return sum(
        input.accommodationTax ?? 0,
        input.breakfast ?? 0,
        input.depositInitialGuaranteeFee ?? 0
    )
}

export const calculateReservationSalesPrice = (
    input: ReservationSalesPriceInput
): number => {
    return (
        calculateReservationStayTypeSalesPrice(input) +
        calculateReservationAdditionalSalesPrice(input)
    )
}

export const useMatsuriReservationInfo = (
    token: string,
    reservationId: string
) => {
    return useAuthFetch<{ reservationId: string; contractType: string }>(
        token,
        m2mCore.v1.getMatsuriReservationInfo({ id: reservationId }),
        {}
    )
}

export const registerMatsuriReservationInfo = (
    token: string,
    req: { reservationId: string; contractType: string }
) => {
    return fetcher(
        m2mCore.v1.registerMatsuriReservationInfo({ id: req.reservationId }),
        {
            token,
            method: "POST",
            body: JSON.stringify(req)
        }
    )
}

export interface SearchReservationsByOtaInfoResponse {
    reservations?: Reservation[]
}

export const requestSearchReservationsByOtaInfo = (
    token: string,
    req: { otaId: string }
) => {
    return fetcher<SearchReservationsByOtaInfoResponse>(
        m2mCore.v1.searchByOtaInfo(),
        {
            method: "POST",
            token,
            body: JSON.stringify(req)
        }
    )
}

// 日付が同じなら時間は見ないというタイプのSameBefore
export const isTodayBeforeDate = (d: dayjs.Dayjs) => {
    return dayjs().isSameOrBefore(d, "d")
}

export const requestForceUnassignReservation = (
    token: string,
    reservationId: string
) => {
    const endpoint = m2mCore_v1.forceUnassignedReservation

    return fetcher(endpoint({ id: reservationId }), {
        token,
        method: endpoint.method
    })
}

export const requestImportReservations = (
    token: string,
    input: ListingOtaInfo
) => {
    const endpoint = m2mCore_v1.importReservations

    return fetcher(endpoint(), {
        token,
        body: JSON.stringify(input),
        method: endpoint.method
    })
}

export const requestUpdateReservationWithoutAssign = (
    token: string,
    reservationId: string,
    input: {
        reasonUnassigned?: string
        memo?: string
    }
) => {
    const endpoint = m2mCore_v1.updateReservationWithoutAssign

    return fetcher(endpoint({ id: reservationId }), {
        token,
        method: endpoint.method,
        body: JSON.stringify(input)
    })
}

export const requestSplitAndTransferReservation = (
    token: string,
    reservationId: string,
    input: SplitAndTransferReservationRequest
) => {
    const endpoint = m2mCore_v1.splitAndTransferReservation

    return fetcher(endpoint({ reservationId }), {
        token,
        method: endpoint.method,
        body: JSON.stringify(input)
    })
}

export const requestCreateTemporaryTransferReservation = (
    token: string,
    reservationId: string,
    input: CreateTemporaryTransferReservationRequest
) => {
    const endpoint = m2mCore_v1.createTemporaryTransferReservation

    return fetcher(endpoint({ reservationId }), {
        token,
        method: endpoint.method,
        body: JSON.stringify(input)
    })
}
